import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  useAssignLeadsToUserBySearchIdMutation,
  useAssignLeadsToUserMutation,
  useDeleteLeadsBySearchIdMutation,
  useDeleteLeadsMutation,
  useMarkLeadsAsAnsweredBySearchIdMutation,
  useMarkLeadsAsAnsweredMutation,
  useRemoveLeadsTagsMutation,
  useRemoveLeadsTagsBySearchIdMutation,
  useSetLeadsTagsBySearchIdMutation,
  useSetLeadsTagsMutation,
} from '@app/api/Leads';
import {
  useAddOrExchangeRangeBySearchIdMutation,
  useAddOrExchangeRangeMutation,
} from '@app/api/SalesFunnelStageLead/api';
import { useGetAllTagsQuery } from '@app/api/Tags';
import type { MassActionType } from '@app/components/Modals/MassActionTypeModal/types';
import { FRONT_WARNING, SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useLatestRegistryResultBridge } from '@app/pages/Leads/model';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { toTagItems } from '@app/shared/utils';

import { useLeadsRegistrySelection } from './useLeadsRegistrySelection';

export type LeadsRegistryMassAction =
  | 'assign'
  | 'mark-not-requiring-answer'
  | 'funnel-stage'
  | 'sendout'
  | 'add-tags'
  | 'remove-tags'
  | 'delete';

// Registry mass actions are page-local; the list refreshes through the Leads
// realtime subscription, and successful mutations clear the current selection.
export const useLeadsRegistryMassActions = () => {
  const { params } = useUserPrint();
  const activeBranchId = params.ActiveBranchId;
  const { showNotification } = useNotification();
  const { selectedLeadIds, clearSelection } = useLeadsRegistrySelection();
  const { leads, searchId, totalCount, requestRefresh } =
    useLatestRegistryResultBridge();
  const [deleteLeads, { isLoading: isDeletingSelected }] =
    useDeleteLeadsMutation();
  const [deleteLeadsBySearchId, { isLoading: isDeletingAll }] =
    useDeleteLeadsBySearchIdMutation();
  const [assignLeadsToUser, { isLoading: isAssigningSelected }] =
    useAssignLeadsToUserMutation();
  const [assignLeadsToUserBySearchId, { isLoading: isAssigningAll }] =
    useAssignLeadsToUserBySearchIdMutation();
  const [markLeadsAsAnswered, { isLoading: isMarkingAsAnsweredSelected }] =
    useMarkLeadsAsAnsweredMutation();
  const [markLeadsAsAnsweredBySearchId, { isLoading: isMarkingAsAnsweredAll }] =
    useMarkLeadsAsAnsweredBySearchIdMutation();
  const [setLeadsTags, { isLoading: isSettingTags }] =
    useSetLeadsTagsMutation();
  const [setLeadsTagsBySearchId, { isLoading: isSettingTagsBySearchId }] =
    useSetLeadsTagsBySearchIdMutation();
  const [removeLeadsTags, { isLoading: isRemovingTagsSelected }] =
    useRemoveLeadsTagsMutation();
  const [removeLeadsTagsBySearchId, { isLoading: isRemovingTagsBySearchId }] =
    useRemoveLeadsTagsBySearchIdMutation();
  const [changeFunnelStage, { isLoading: isChangingSelectedFunnelStage }] =
    useAddOrExchangeRangeMutation();
  const [
    changeFunnelStageBySearchId,
    { isLoading: isChangingAllFunnelStages },
  ] = useAddOrExchangeRangeBySearchIdMutation();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState<number | null>(null);
  const [isMassActionTypeModalOpen, setIsMassActionTypeModalOpen] =
    useState(false);
  const [pendingMassAction, setPendingMassAction] =
    useState<LeadsRegistryMassAction | null>(null);
  const [massActionType, setMassActionType] = useState<MassActionType | null>(
    null
  );
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [isRemoveTagsModalOpen, setIsRemoveTagsModalOpen] = useState(false);
  const [removeTagIds, setRemoveTagIds] = useState<number[]>([]);
  const [isFunnelStageModalOpen, setIsFunnelStageModalOpen] = useState(false);

  const isDeleting = isDeletingSelected || isDeletingAll;
  const isAssigning = isAssigningSelected || isAssigningAll;
  const isMarkingAsAnswered =
    isMarkingAsAnsweredSelected || isMarkingAsAnsweredAll;
  const isChangingFunnelStage =
    isChangingSelectedFunnelStage || isChangingAllFunnelStages;
  const isTagsSaving = isSettingTags || isSettingTagsBySearchId;
  const isRemovingTags = isRemovingTagsSelected || isRemovingTagsBySearchId;
  // `SearchId` — int64. `Number(searchId)` округлял его до ближайшего double
  // (шаг 128 на таких значениях), бэкенд получал чужой id и отвечал
  // «Поиск лидов с ID ... не найден». Держим id строкой на всём пути.
  const currentSearchId = searchId?.trim() ?? '';
  const hasSearchId = /^\d+$/.test(currentSearchId);
  // Рассылка отправляется не сразу после выбора «все по фильтру»: пока форма
  // заполняется, реестр может выполнить новый поиск, и прежняя группа на
  // сервере удаляется (`ListenLeadsGroup(..., deleteSearchId)`). Поэтому
  // модалка рассылки получает не значение, а геттер — id берётся в момент
  // отправки (issue/11096).
  const searchIdRef = useRef(currentSearchId);
  searchIdRef.current = currentSearchId;

  const getCurrentSearchId = useCallback(
    () => searchIdRef.current || undefined,
    []
  );
  const allCount = totalCount ?? leads.length;
  const hasLeads = leads.length > 0 || allCount > 0;

  const { data: tagsData, isFetching: isTagsLoading } = useGetAllTagsQuery(
    (isTagsModalOpen || isRemoveTagsModalOpen) && activeBranchId
      ? { ActiveBranchId: activeBranchId }
      : skipToken
  );

  const availableTags = useMemo(() => toTagItems(tagsData), [tagsData]);

  const isMassActionTargetValid = useCallback(
    (type: MassActionType | null) => {
      if (type === null) return false;

      if (type === 'selected' && selectedLeadIds.length === 0) {
        showNotification('Не выбраны лиды', FRONT_WARNING);
        return false;
      }

      if (type === 'all' && !hasSearchId) {
        showNotification(
          'Идентификатор поискового запроса не найден',
          FRONT_WARNING
        );
        return false;
      }

      return true;
    },
    [hasSearchId, selectedLeadIds.length, showNotification]
  );

  const openMassActionTypeModal = useCallback(
    (action: LeadsRegistryMassAction) => {
      setPendingMassAction(action);
      setMassActionType(null);
      if (action === 'add-tags') {
        setSelectedTagIds([]);
      }
      if (action === 'remove-tags') {
        setRemoveTagIds([]);
      }
      setIsMassActionTypeModalOpen(true);
    },
    []
  );

  const closeMassActionTypeModal = useCallback(() => {
    setIsMassActionTypeModalOpen(false);
    setPendingMassAction(null);
  }, []);

  const confirmMassActionType = useCallback(
    (type: MassActionType) => {
      if (pendingMassAction === null) return false;
      if (!isMassActionTargetValid(type)) return false;

      setMassActionType(type);

      switch (pendingMassAction) {
        case 'assign':
          setIsAssignModalOpen(true);
          break;
        case 'funnel-stage':
          setIsFunnelStageModalOpen(true);
          break;
        case 'add-tags':
          setIsTagsModalOpen(true);
          break;
        case 'remove-tags':
          setIsRemoveTagsModalOpen(true);
          break;
        case 'delete':
          setIsDeleteModalOpen(true);
          break;
        case 'mark-not-requiring-answer':
        case 'sendout':
          break;
      }

      return true;
    },
    [isMassActionTargetValid, pendingMassAction]
  );

  const closeTagsModal = useCallback(() => {
    if (isTagsSaving) return;

    setIsTagsModalOpen(false);
    setSelectedTagIds([]);
    setMassActionType(null);
  }, [isTagsSaving]);

  const closeRemoveTagsModal = useCallback(() => {
    if (isRemovingTags) return;

    setIsRemoveTagsModalOpen(false);
    setRemoveTagIds([]);
    setMassActionType(null);
  }, [isRemovingTags]);

  const confirmDelete = useCallback(async () => {
    if (!activeBranchId || !isMassActionTargetValid(massActionType)) return;

    try {
      if (massActionType === 'all') {
        await deleteLeadsBySearchId({
          ActiveBranchId: activeBranchId,
          searchId: currentSearchId,
        }).unwrap();
      } else {
        await deleteLeads({
          ActiveBranchId: activeBranchId,
          data: selectedLeadIds,
        }).unwrap();
      }

      clearSelection();
      setIsDeleteModalOpen(false);
      setMassActionType(null);
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [
    activeBranchId,
    clearSelection,
    deleteLeads,
    deleteLeadsBySearchId,
    isMassActionTargetValid,
    massActionType,
    currentSearchId,
    selectedLeadIds,
    showNotification,
  ]);

  const confirmAssign = useCallback(async () => {
    if (
      !activeBranchId ||
      assignUserId === null ||
      !isMassActionTargetValid(massActionType)
    ) {
      return;
    }

    try {
      if (massActionType === 'all') {
        await assignLeadsToUserBySearchId({
          ActiveBranchId: activeBranchId,
          forUserId: assignUserId,
          searchId: currentSearchId,
        }).unwrap();
      } else {
        await assignLeadsToUser({
          ActiveBranchId: activeBranchId,
          forUserId: assignUserId,
          data: selectedLeadIds,
        }).unwrap();
      }

      clearSelection();
      setIsAssignModalOpen(false);
      setAssignUserId(null);
      setMassActionType(null);
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [
    activeBranchId,
    assignLeadsToUser,
    assignLeadsToUserBySearchId,
    assignUserId,
    clearSelection,
    isMassActionTargetValid,
    massActionType,
    currentSearchId,
    selectedLeadIds,
    showNotification,
  ]);

  // No extra parameters to collect, so this runs right after the scope
  // (selected/all) is picked in MassActionTypeModal — see 'sendout' handling
  // in the component for the same no-modal pattern.
  const confirmMarkNotRequiringAnswer = useCallback(
    async (type: MassActionType) => {
      if (!activeBranchId || !isMassActionTargetValid(type)) return;

      try {
        if (type === 'all') {
          await markLeadsAsAnsweredBySearchId({
            ActiveBranchId: activeBranchId,
            searchId: currentSearchId,
          }).unwrap();
        } else {
          await markLeadsAsAnswered({
            ActiveBranchId: activeBranchId,
            data: selectedLeadIds,
          }).unwrap();
        }

        showNotification('Лиды отмечены как не требующие ответа', SUCCESS);
        // This mutation doesn't come with a realtime group-update push (unlike
        // delete/assign/funnel-stage), so the list needs an explicit refetch —
        // same reason confirmSetTags/confirmRemoveTags call it below.
        requestRefresh();
        clearSelection();
        setMassActionType(null);
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [
      activeBranchId,
      clearSelection,
      isMassActionTargetValid,
      currentSearchId,
      markLeadsAsAnswered,
      markLeadsAsAnsweredBySearchId,
      requestRefresh,
      selectedLeadIds,
      showNotification,
    ]
  );

  const confirmSetTags = useCallback(async () => {
    if (!activeBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    if (selectedTagIds.length === 0) {
      showNotification('Выберите хотя бы один тег', FRONT_WARNING);
      return;
    }

    if (!isMassActionTargetValid(massActionType)) return;

    try {
      if (massActionType === 'all') {
        await setLeadsTagsBySearchId({
          ActiveBranchId: activeBranchId,
          data: {
            SearchId: currentSearchId,
            TagIds: selectedTagIds,
          },
        }).unwrap();
      } else {
        await setLeadsTags({
          ActiveBranchId: activeBranchId,
          data: {
            LeadIds: selectedLeadIds,
            TagIds: selectedTagIds,
          },
        }).unwrap();
      }

      showNotification('Теги успешно добавлены', SUCCESS);
      requestRefresh();
      clearSelection();
      setIsTagsModalOpen(false);
      setSelectedTagIds([]);
      setMassActionType(null);
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [
    activeBranchId,
    clearSelection,
    isMassActionTargetValid,
    massActionType,
    currentSearchId,
    requestRefresh,
    selectedLeadIds,
    selectedTagIds,
    setLeadsTagsBySearchId,
    setLeadsTags,
    showNotification,
  ]);

  const confirmRemoveTags = useCallback(async () => {
    if (!activeBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    if (removeTagIds.length === 0) {
      showNotification('Выберите хотя бы один тег', FRONT_WARNING);
      return;
    }

    if (!isMassActionTargetValid(massActionType)) return;

    try {
      if (massActionType === 'all') {
        await removeLeadsTagsBySearchId({
          ActiveBranchId: activeBranchId,
          data: {
            SearchId: currentSearchId,
            TagIds: removeTagIds,
          },
        }).unwrap();
      } else {
        await removeLeadsTags({
          ActiveBranchId: activeBranchId,
          data: {
            LeadIds: selectedLeadIds,
            TagIds: removeTagIds,
          },
        }).unwrap();
      }

      showNotification('Теги успешно сняты', SUCCESS);
      requestRefresh();
      clearSelection();
      setIsRemoveTagsModalOpen(false);
      setRemoveTagIds([]);
      setMassActionType(null);
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [
    activeBranchId,
    clearSelection,
    isMassActionTargetValid,
    massActionType,
    currentSearchId,
    requestRefresh,
    removeLeadsTags,
    removeLeadsTagsBySearchId,
    removeTagIds,
    selectedLeadIds,
    showNotification,
  ]);

  const confirmChangeFunnelStage = useCallback(
    async (funnelStageId: number) => {
      if (!activeBranchId || !isMassActionTargetValid(massActionType)) return;

      try {
        if (massActionType === 'all') {
          await changeFunnelStageBySearchId({
            ActiveBranchId: activeBranchId,
            funnelStageId,
            searchId: currentSearchId,
          }).unwrap();
        } else {
          await changeFunnelStage({
            ActiveBranchId: activeBranchId,
            funnelStageId,
            data: selectedLeadIds,
          }).unwrap();
        }

        clearSelection();
        setIsFunnelStageModalOpen(false);
        setMassActionType(null);
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [
      activeBranchId,
      changeFunnelStage,
      changeFunnelStageBySearchId,
      clearSelection,
      isMassActionTargetValid,
      massActionType,
      currentSearchId,
      selectedLeadIds,
      showNotification,
    ]
  );

  // Sendout targets clients, so selected lead ids are mapped to their client ids
  // (matching the legacy registry mass "send message" action).
  const selectedClientIds = useMemo(() => {
    const clientIdByLeadId = new Map(
      leads.map((lead) => [lead.Id, lead.ClientId])
    );

    return selectedLeadIds
      .map((leadId) => clientIdByLeadId.get(leadId))
      .filter((clientId): clientId is number => clientId != null);
  }, [leads, selectedLeadIds]);

  return {
    activeBranchId,
    searchId,
    getCurrentSearchId,
    allCount,
    hasLeads,
    selectedClientIds,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    isDeleting,
    confirmDelete,
    isAssignModalOpen,
    setIsAssignModalOpen,
    assignUserId,
    setAssignUserId,
    isAssigning,
    confirmAssign,
    isMarkingAsAnswered,
    confirmMarkNotRequiringAnswer,
    pendingMassAction,
    massActionType,
    isMassActionTypeModalOpen,
    openMassActionTypeModal,
    closeMassActionTypeModal,
    confirmMassActionType,
    isTagsModalOpen,
    closeTagsModal,
    selectedTagIds,
    setSelectedTagIds,
    availableTags,
    isTagsLoading,
    isTagsSaving,
    confirmSetTags,
    isRemoveTagsModalOpen,
    closeRemoveTagsModal,
    removeTagIds,
    setRemoveTagIds,
    isRemovingTags,
    confirmRemoveTags,
    isFunnelStageModalOpen,
    setIsFunnelStageModalOpen,
    isChangingFunnelStage,
    confirmChangeFunnelStage,
  };
};
